import { styled } from 'baseui'
import { FlexColumn, FlexCenterY } from '@/shared/theme/styled-components'
import CustomToggleIcon from './CustomToggleIcon/CustomToggleIcon'
import PulseToggleIcon from './PulseToggleIcon/PulseToggleIcon'

export const StyledIcon = styled('img', ({ $pulse }) =>
  $pulse
    ? { width: '18px', height: '18px', marginRight: '10px' }
    : {
        width: '1.5rem',
        height: '1.5rem',
        marginRight: '.25rem',
      },
)

export const StyledLogoSlot = styled(FlexCenterY, ({ $pulse }) =>
  $pulse
    ? { height: '18px', marginRight: '10px' }
    : {
        height: '1.5rem',
        marginRight: '0.3125rem',
      },
)

export const StyledTitle = styled('h3', ({ $theme, $pulse }) => ({
  flexGrow: 1,
  marginTop: 0,
  marginBottom: 0,
  marginLeft: 0,
  marginRight: 0,
  fontWeight: 600,
  ...($pulse
    ? { fontSize: '15px', lineHeight: '20px', color: $theme.colors.textPulseStrong }
    : {
        fontSize: '18px',
        letterSpacing: '-1.4%',
        color: $theme.colors.textHeading,
        height: '1.5rem',
      }),
}))

export const StyledTitleContainer = styled(FlexColumn, {})

export const StyledPanelWrapper = styled(FlexColumn, ({ $theme, $pulse, $collapsed, $flashed }) =>
  $pulse
    ? {
        alignItems: 'stretch',
        backgroundColor: $theme.colors.white,
        borderTopWidth: '1px',
        borderBottomWidth: '1px',
        borderLeftWidth: '1px',
        borderRightWidth: '1px',
        borderTopStyle: 'solid',
        borderBottomStyle: 'solid',
        borderLeftStyle: 'solid',
        borderRightStyle: 'solid',
        borderTopColor: $flashed ? $theme.colors.missPulseBorder : $theme.colors.borderPulseCard,
        borderBottomColor: $flashed ? $theme.colors.missPulseBorder : $theme.colors.borderPulseCard,
        borderLeftColor: $flashed ? $theme.colors.missPulseBorder : $theme.colors.borderPulseCard,
        borderRightColor: $flashed ? $theme.colors.missPulseBorder : $theme.colors.borderPulseCard,
        borderTopLeftRadius: '16px',
        borderTopRightRadius: '16px',
        borderBottomLeftRadius: '16px',
        borderBottomRightRadius: '16px',
        boxShadow: $flashed ? $theme.lighting.shadowPulseFlash : 'none',
        transitionProperty: 'border-color, box-shadow',
        transitionDuration: '200ms',
        paddingTop: '16px',
        paddingBottom: $collapsed ? '16px' : '22px',
        paddingLeft: '22px',
        paddingRight: '22px',
        minWidth: 0,
        scrollMarginTop: '16px',
      }
    : {
        alignItems: 'flex-start',
        alignSelf: 'stretch',
        marginBottom: '0.9375rem',
      },
)

export const StyledHeaderContainer = styled(FlexCenterY, {
  width: '100%',
})

export const StyledSubHeaderContainer = styled('div', ({ $theme, $pulse }) =>
  $pulse
    ? { color: $theme.colors.textPulseMuted, fontSize: '13px', lineHeight: '18px', marginTop: '2px' }
    : {
        color: $theme.colors.grey,
        fontWeight: 400,
        letterSpacing: '-0.008px',
        fontSize: '0.875rem',
        marginTop: '.25rem',
      },
)

const pulseAccordionMotion = {
  transitionDuration: '300ms',
  transitionTimingFunction: 'ease',
  '@media (prefers-reduced-motion: reduce)': { transitionDuration: '0ms' },
}

export const PulsePanelOverrides = {
  Header: {
    style: ({ $theme }) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '10px',
      width: '100%',
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
      backgroundColor: 'transparent',
      ':hover': {
        color: $theme.colors.textPulseStrong,
      },
    }),
  },
  PanelContainer: {
    style: {
      borderBottomWidth: 0,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  ContentAnimationContainer: {
    style: ({ $expanded }) => ({
      ...pulseAccordionMotion,
      height: 'auto',
      display: 'grid',
      gridTemplateRows: $expanded ? '1fr' : '0fr',
      transitionProperty: 'grid-template-rows',
    }),
  },
  Content: {
    style: ({ $expanded }) => ({
      ...pulseAccordionMotion,
      transitionProperty: 'opacity, visibility, padding-top',
      transitionDelay: '0ms',
      minHeight: 0,
      backgroundColor: 'transparent',
      marginTop: 0,
      marginBottom: 0,
      marginLeft: 0,
      marginRight: 0,
      paddingTop: $expanded ? '16px' : 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
    }),
  },
  ToggleIcon: {
    component: PulseToggleIcon,
  },
}

export const PanelOverrides = {
  Header: {
    style: ({ $theme }) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      paddingTop: '0rem',
      paddingBottom: '0rem',
      paddingLeft: '0rem',
      paddingRight: '0rem',
      ':hover': {
        color: $theme.colors.black,
      },
    }),
  },
  PanelContainer: {
    style: {
      borderBottomWidth: '0rem',
      paddingTop: '0.6rem',
      paddingBottom: '0.6rem',
      paddingLeft: '0rem',
      paddingRight: '0rem',
    },
  },
  Content: {
    style: {
      marginTop: '1.25rem',
      marginBottom: '0rem',
      marginLeft: '0rem',
      marginRight: '0rem',
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  ToggleIcon: {
    component: CustomToggleIcon,
  },
}
